import { VIEWER_AVATAR_IMAGE_URL_MAX_LENGTH } from "@refinement-canvas/domain";
import { withAuthOperationTimeout } from "@osakabe/auth-service-provider";
import { useEffect, useMemo, useState } from "react";
import { getWebConfig } from "../env";

type IdpFileProtocolResolver = (idpFileUrl: string) => Promise<Blob>;

const AVATAR_EDGE_CANDIDATES = [96, 72, 48, 32] as const;
const AVATAR_QUALITY_CANDIDATES = [0.82, 0.72, 0.62, 0.52, 0.42] as const;
const DATA_IMAGE_PREFIX = /^data:image\//iu;
const IDP_FILE_PREFIX = "idp-file:";
const PORTABLE_AVATAR_CACHE_MAX_ENTRIES = 256;

interface PortableAvatarImageState {
  readonly cacheKey: string | undefined;
  readonly imageUrl: string | undefined;
}

interface CachedPortableAvatarImageUrl {
  readonly imageUrl: string;
}

const portableAvatarImageUrlCache = new Map<string, CachedPortableAvatarImageUrl>();
const inFlightPortableAvatarLoads = new Map<string, Promise<string | undefined>>();

// WebSocket / HTTP ヘッダーで安全に配れるアバター画像 URL へ正規化する。
// idp-file や巨大な data URL は、ブラウザ側で小さな data URL に圧縮してから配信する。
export function usePortableAvatarImageUrl(
  src: string | null | undefined,
  resolveIdpFile?: IdpFileProtocolResolver | undefined,
  retryToken = 0
): string | undefined {
  const sanitizedSrc = useMemo(() => sanitizeAvatarImageUrl(src), [src]);
  const directSrc = getDirectPortableAvatarImageUrl(sanitizedSrc);
  const cacheKey =
    directSrc === undefined && sanitizedSrc !== undefined
      ? toPortableAvatarCacheKey(sanitizedSrc)
      : undefined;
  const [portableState, setPortableState] = useState<PortableAvatarImageState>(() => ({
    cacheKey,
    imageUrl: cacheKey !== undefined ? peekCachedPortableAvatarImageUrl(cacheKey) : directSrc,
  }));
  const portableSrc =
    portableState.cacheKey === cacheKey
      ? portableState.imageUrl
      : cacheKey !== undefined
        ? peekCachedPortableAvatarImageUrl(cacheKey)
        : directSrc;

  useEffect(() => {
    let cancelled = false;
    const nextDirectSrc = getDirectPortableAvatarImageUrl(sanitizedSrc);
    if (nextDirectSrc !== undefined || sanitizedSrc === undefined) {
      setPortableState({
        cacheKey: undefined,
        imageUrl: nextDirectSrc,
      });
      return () => {
        cancelled = true;
      };
    }

    const nextCacheKey = toPortableAvatarCacheKey(sanitizedSrc);
    const cachedImageUrl = readCachedPortableAvatarImageUrl(nextCacheKey);
    if (cachedImageUrl !== undefined) {
      setPortableState({
        cacheKey: nextCacheKey,
        imageUrl: cachedImageUrl,
      });
      return () => {
        cancelled = true;
      };
    }

    setPortableState((current) =>
      current.cacheKey === nextCacheKey
        ? current
        : {
            cacheKey: nextCacheKey,
            imageUrl: undefined,
          }
    );

    void (async () => {
      const compactDataUrl = await loadCachedPortableAvatarImageUrl(nextCacheKey, async () => {
        const blob = await withAuthOperationTimeout(
          "meiden-connect-avatar-load",
          loadAvatarBlob(sanitizedSrc, resolveIdpFile)
        );
        return await createCompactAvatarDataUrl(blob);
      });
      if (!cancelled) {
        setPortableState((current) => {
          if (compactDataUrl === undefined) {
            // 既に共有用 URL を作成済みの場合は、再取得の一時失敗で
            // ヘッダーや presence のアバターを文字アイコンへ戻さない。
            return current.cacheKey === nextCacheKey && current.imageUrl !== undefined
              ? current
              : {
                  cacheKey: nextCacheKey,
                  imageUrl: undefined,
                };
          }

          return {
            cacheKey: nextCacheKey,
            imageUrl: compactDataUrl,
          };
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolveIdpFile, retryToken, sanitizedSrc]);

  return portableSrc;
}

async function loadCachedPortableAvatarImageUrl(
  cacheKey: string,
  loadImageUrl: () => Promise<string | undefined>
): Promise<string | undefined> {
  const cachedImageUrl = readCachedPortableAvatarImageUrl(cacheKey);
  if (cachedImageUrl !== undefined) {
    return cachedImageUrl;
  }

  const existingLoad = inFlightPortableAvatarLoads.get(cacheKey);
  if (existingLoad !== undefined) {
    return existingLoad;
  }

  const loadPromise = (async () => {
    try {
      const imageUrl = await loadImageUrl();
      if (imageUrl !== undefined) {
        rememberPortableAvatarImageUrl(cacheKey, imageUrl);
      }
      return imageUrl;
    } catch (error) {
      // 画像が取得できない場合はアプリ本体を止めず、通常のフォールバック表示へ倒す。
      console.warn("[avatar] アバター画像の縮小に失敗しました", error);
      return undefined;
    }
  })().finally(() => {
    if (inFlightPortableAvatarLoads.get(cacheKey) === loadPromise) {
      inFlightPortableAvatarLoads.delete(cacheKey);
    }
  });

  inFlightPortableAvatarLoads.set(cacheKey, loadPromise);
  return loadPromise;
}

function toPortableAvatarCacheKey(src: string): string {
  return src;
}

function readCachedPortableAvatarImageUrl(cacheKey: string): string | undefined {
  const cached = portableAvatarImageUrlCache.get(cacheKey);
  if (cached === undefined) {
    return undefined;
  }

  // Map の挿入順を LRU として使うため、参照された entry を末尾へ移動する。
  portableAvatarImageUrlCache.delete(cacheKey);
  portableAvatarImageUrlCache.set(cacheKey, cached);
  return cached.imageUrl;
}

function peekCachedPortableAvatarImageUrl(cacheKey: string): string | undefined {
  return portableAvatarImageUrlCache.get(cacheKey)?.imageUrl;
}

function rememberPortableAvatarImageUrl(cacheKey: string, imageUrl: string): void {
  portableAvatarImageUrlCache.set(cacheKey, { imageUrl });

  while (portableAvatarImageUrlCache.size > PORTABLE_AVATAR_CACHE_MAX_ENTRIES) {
    const oldestKey = portableAvatarImageUrlCache.keys().next().value as string | undefined;
    if (oldestKey === undefined) {
      return;
    }
    portableAvatarImageUrlCache.delete(oldestKey);
  }
}

export function clearPortableAvatarImageUrlCacheForTest(): void {
  portableAvatarImageUrlCache.clear();
  inFlightPortableAvatarLoads.clear();
}

export function sanitizeAvatarImageUrl(src: string | null | undefined): string | undefined {
  if (src == null) return undefined;
  const trimmed = src.trim();
  if (trimmed.length === 0) return undefined;
  if (trimmed === "null" || trimmed === "undefined") return undefined;
  return trimmed;
}

export interface RenderableAvatarImageUrlOptions {
  /**
   * https アバターの描画を許可する origin の allowlist。
   * 指定時は、その origin に一致する https URL のみ許可する（他参加者由来のリモート表示で使う）。
   * 未指定（自分自身のアバター等）は、従来どおり任意の https を許容する。
   */
  readonly allowedHttpsOrigins?: readonly string[] | undefined;
}

/**
 * <img src> として描画してよいアバター URL かどうかを判定する。
 * 正規のアバターは data:image/...（圧縮済みの縮小画像）か IdP の https URL のみ。
 * 他参加者が viewer.sync 等で送り込む任意 URL（外部追跡用の http 画像、javascript: 等）を弾き、
 * クロスユーザーの強制リクエストやトラッキングを防ぐ多層防御として描画直前の境界で使う。
 *
 * リモート参加者由来の表示では allowedHttpsOrigins（信頼済み origin）を渡し、任意の https を
 * 許可しない。data:image/ はローカルで圧縮済みのプレースホルダ/縮小画像のため常に許可する。
 */
export function isRenderableAvatarImageUrl(
  src: string | null | undefined,
  options: RenderableAvatarImageUrlOptions = {}
): boolean {
  const sanitized = sanitizeAvatarImageUrl(src);
  if (sanitized === undefined) return false;
  if (/^data:image\//iu.test(sanitized)) return true;
  try {
    const url = new URL(sanitized);
    if (url.protocol !== "https:") return false;
    const allowedHttpsOrigins = options.allowedHttpsOrigins;
    // allowlist 未指定なら従来どおり任意 https を許容（自分自身のアバター用）。
    // allowlist 指定時（リモート表示）は許可 origin のみ許容し、任意 https による
    // 強制外部リクエスト/トラッキングを防ぐ。
    return allowedHttpsOrigins === undefined || allowedHttpsOrigins.includes(url.origin);
  } catch {
    return false;
  }
}

/**
 * リモート参加者由来のアバターを https で描画してよい origin の allowlist を返す。
 * 信頼済みの IdP（authServiceOrigin）のみを許可する。WebConfig 未初期化時（テスト等）は
 * 空配列を返し、https を一切許可しない（data:image/ のみ許可）安全側にフォールバックする。
 */
export function getRemoteAvatarAllowedHttpsOrigins(): readonly string[] {
  try {
    const origin = getWebConfig().authServiceOrigin;
    return origin ? [origin] : [];
  } catch {
    return [];
  }
}

function getDirectPortableAvatarImageUrl(src: string | undefined): string | undefined {
  if (src === undefined) return undefined;
  if (src.startsWith(IDP_FILE_PREFIX)) return undefined;
  if (src.length > VIEWER_AVATAR_IMAGE_URL_MAX_LENGTH) return undefined;
  return src;
}

async function loadAvatarBlob(
  src: string,
  resolveIdpFile: IdpFileProtocolResolver | undefined
): Promise<Blob> {
  if (src.startsWith(IDP_FILE_PREFIX)) {
    if (!resolveIdpFile) {
      throw new Error("idp-file アバターを解決する関数がありません。");
    }
    return withAuthOperationTimeout("meiden-connect-avatar-idp-file-resolve", resolveIdpFile(src));
  }
  if (!DATA_IMAGE_PREFIX.test(src)) {
    throw new Error("縮小できるアバター画像 URL ではありません。");
  }
  const response = await fetch(src);
  return response.blob();
}

async function createCompactAvatarDataUrl(blob: Blob): Promise<string | undefined> {
  const image = await loadImage(blob);
  try {
    const maxEdge = Math.max(image.naturalWidth, image.naturalHeight, 1);
    for (const edge of AVATAR_EDGE_CANDIDATES) {
      const scale = Math.min(1, edge / maxEdge);
      const width = Math.max(1, Math.round(image.naturalWidth * scale));
      const height = Math.max(1, Math.round(image.naturalHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) continue;
      context.clearRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      const dataUrl = pickSmallestDataUrl(canvas);
      if (dataUrl !== undefined && dataUrl.length <= VIEWER_AVATAR_IMAGE_URL_MAX_LENGTH) {
        return dataUrl;
      }
    }
    return undefined;
  } finally {
    URL.revokeObjectURL(image.src);
  }
}

function pickSmallestDataUrl(canvas: HTMLCanvasElement): string | undefined {
  const candidates: string[] = [];
  for (const quality of AVATAR_QUALITY_CANDIDATES) {
    const webp = canvas.toDataURL("image/webp", quality);
    if (webp.startsWith("data:image/webp")) {
      candidates.push(webp);
    }
    const jpeg = canvas.toDataURL("image/jpeg", quality);
    if (jpeg.startsWith("data:image/jpeg")) {
      candidates.push(jpeg);
    }
  }
  const png = canvas.toDataURL("image/png");
  if (png.startsWith("data:image/png")) {
    candidates.push(png);
  }
  return candidates.sort((left, right) => left.length - right.length)[0];
}

async function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const src = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => {
      URL.revokeObjectURL(src);
      reject(new Error("アバター画像の読み込みに失敗しました。"));
    };
    image.src = src;
  });
}
